import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Modal,
  Button,
  message,
  Input,
  Select,
  InputNumber,
  Radio,
} from 'antd';
import { useTranslation } from 'react-i18next';
import api from '@services/api';
import { toBytes } from '@utils/formatters';
import SessionContext from '@providers/Session/SessionContext';

const { Option } = Select;

type ModalCreateAlertsProps = {
  visible: boolean;
  onClose: () => void;
  mutate: (url: string) => void;
};

const ModalCreateAlerts = ({
  visible,
  onClose,
  mutate,
}: ModalCreateAlertsProps) => {
  const { t } = useTranslation(['alerts', 'attributes']);
  const { theme, isMotEnterprise, isMotBusiness } = useContext(SessionContext);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [field, setField] = useState<string | undefined>();
  const [thresholdValue, setThresholdValue] = useState<string | null>('0');
  const [name, setName] = useState<string | undefined>();
  const [eventType, setEventType] = useState('CONSUMPTION');
  const [unit, setUnit] = useState<'KB' | 'MB' | 'GB'>('MB');

  const createAlert = useCallback(
    async (payload: {
      field: string | undefined;
      name: string | undefined;
      thresholdValue: string | number | null;
    }) => {
      await api.post('/service-proxy/alerts', {
        ...payload,
        eventType,
        operator: '>',
      });

      mutate('/service-proxy/alerts');
    },
    [mutate, eventType],
  );

  const handleClose = useCallback(() => {
    setName(undefined);
    setField(undefined);
    setThresholdValue('0');
    setEventType('CONSUMPTION');
    setUnit('MB');
    onClose();
  }, [onClose]);

  const handleFinish = useCallback(async () => {
    setConfirmLoading(true);

    const calculateThresholdValue = () => {
      if (field === 'consumptionPercent') return thresholdValue;
      if (
        field === 'consumptionTotalAnomaly' ||
        field === 'consumptionCurrentDayAnomaly'
      ) {
        return Number(thresholdValue) / 100;
      }

      return toBytes(Number(thresholdValue), unit);
    };

    try {
      const payload = {
        field,
        name,
        thresholdValue: calculateThresholdValue(),
      };

      await createAlert(payload);
      message.success(t('msgs.alert-created'));
      handleClose();
    } catch (error: any) {
      const isMaxAlertsReached =
        error.response?.status === 400 &&
        error.response?.data === 'MAX_ALERTS_REACHED';

      if (isMaxAlertsReached) {
        message.error(t('msgs.max-alerts'));
      } else {
        message.error(t('msgs.alert-creation-error'));
      }

      handleClose();
    } finally {
      setConfirmLoading(false);
    }
  }, [field, thresholdValue, unit, createAlert, name, t, handleClose]);

  useEffect(() => {
    if (
      field === 'consumptionTotalAnomaly' ||
      field === 'consumptionCurrentDayAnomaly'
    ) {
      setThresholdValue('101');
    }
    if (field === 'consumptionPercent') {
      setThresholdValue('100');
    }
    if (field === 'consumptionCurrentDay' || field === 'consumptionTotal') {
      setThresholdValue('0');
    }
  }, [visible, field]);

  // Reset unit to MB when field changes to non-consumption fields
  useEffect(() => {
    if (
      field === 'consumptionPercent' ||
      field === 'consumptionTotalAnomaly' ||
      field === 'consumptionCurrentDayAnomaly'
    ) {
      setUnit('MB');
    }
  }, [field]);

  return (
    <Modal
      bodyStyle={{ minHeight: 180, padding: 24 }}
      footer={
        <Button
          loading={confirmLoading}
          type="primary"
          onClick={handleFinish}
          disabled={!name || !field || thresholdValue === null}
        >
          {t('buttons.confirm')}
        </Button>
      }
      maskClosable={false}
      title={t('titles.create-alert')}
      open={visible}
      centered
      onCancel={handleClose}
    >
      <div style={{ marginBottom: 16 }}>
        <p style={{ marginBottom: 4, color: theme?.colors?.text1 }}>
          {t('table.eventType')}
        </p>
        <Radio.Group
          onChange={e => {
            setEventType(e.target.value);
            setField(undefined);
          }}
          value={eventType}
          style={{
            marginBottom: 16,
          }}
          optionType="button"
        >
          <Radio value="CONSUMPTION">{t('eventType.CONSUMPTION')}</Radio>
          {(isMotEnterprise || isMotBusiness) && (
            <Radio value="CONTRACT_CONSUMPTION">
              {t(
                `eventType.${
                  isMotEnterprise ? 'CONTRACT' : 'SHARED_PLAN'
                }_CONSUMPTION`,
              )}
            </Radio>
          )}
        </Radio.Group>
      </div>

      <div style={{ marginBottom: 16 }}>
        <span style={{ color: theme?.colors?.text1 }}>
          {t('attributes:name')}
        </span>
        <Input value={name || ''} onChange={e => setName(e.target.value)} />
      </div>

      <div style={{ marginBottom: 16 }}>
        <span style={{ color: theme?.colors?.text1 }}>{t('table.field')}</span>
        <Select
          value={field}
          onChange={value => setField(value)}
          style={{ width: '100%' }}
        >
          <Option value="consumptionTotal">
            {t('field.consumptionTotal')}
          </Option>
          <Option value="consumptionPercent">
            {t('field.consumptionPercent')}
          </Option>
          <Option value="consumptionTotalAnomaly">
            {t('field.consumptionTotalAnomaly')}
          </Option>

          {eventType === 'CONSUMPTION' && (
            <>
              <Option value="consumptionCurrentDayAnomaly">
                {t('field.consumptionCurrentDayAnomaly')}
              </Option>
              <Option value="consumptionCurrentDay">
                {t('field.consumptionCurrentDay')}
              </Option>
            </>
          )}
        </Select>
      </div>

      <div style={{ marginBottom: 16 }}>
        <span style={{ color: theme?.colors?.text1 }}>
          {t('table.thresholdValue')}
        </span>
        <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
          <InputNumber
            value={thresholdValue}
            onChange={value => setThresholdValue(value)}
            style={{ width: '80px' }}
            min={
              field === 'consumptionTotalAnomaly' ||
              field === 'consumptionCurrentDayAnomaly'
                ? '101'
                : undefined
            }
          />
          {(field === 'consumptionCurrentDay' ||
            field === 'consumptionTotal') && (
            <Select
              value={unit}
              onChange={value => setUnit(value)}
              style={{ width: 64, marginLeft: 8 }}
            >
              <Option value="MB">MB</Option>
              <Option value="KB">KB</Option>
              <Option value="B">B</Option>
            </Select>
          )}
          <span style={{ marginLeft: 8, color: theme?.colors?.text1 }}>
            {field === 'consumptionPercent' ||
            field === 'consumptionTotalAnomaly' ||
            field === 'consumptionCurrentDayAnomaly'
              ? `% ${
                  field === 'consumptionTotalAnomaly' ||
                  field === 'consumptionCurrentDayAnomaly'
                    ? 'do valor esperado'
                    : ''
                }`
              : ''}
          </span>
        </div>
      </div>
    </Modal>
  );
};

export default ModalCreateAlerts;
